// Shared client helpers. The client only displays state and submits intent; it never supplies authority.
export interface ApiError { status: number; code: string; message: string; hold?: boolean; detail?: Record<string, unknown> }
export type Json = any;

export async function api(method: "GET" | "POST", path: string, body?: unknown): Promise<Json> {
  const res = await fetch(path, {
    method, credentials: "same-origin",
    headers: { "content-type": "application/json", "x-b7b-request": "1" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw { status: res.status, ...data } as ApiError;
  return data;
}

export function newKey(prefix: string): string {
  return `${prefix}-${crypto.randomUUID()}`;
}

type Child = Node | string | number | null | undefined | false | Child[];

export function h(tag: string, attrs: Record<string, unknown> = {}, ...children: Child[]): HTMLElement {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v === undefined || v === null || v === false) continue;
    if (k.startsWith("on") && typeof v === "function") el.addEventListener(k.slice(2).toLowerCase(), v as EventListener);
    else if (k === "class") el.className = String(v);
    // CSP style-src 'self' blocks style ATTRIBUTES; CSSOM assignment is allowed and keeps the policy strict
    else if (k === "style") el.style.cssText = String(v);
    else if (k === "value" && (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement)) el.value = String(v);
    else if (v === true) el.setAttribute(k, "");
    else el.setAttribute(k, String(v));
  }
  append(el, children);
  return el;
}

function append(el: Node, children: Child[]): void {
  for (const c of children) {
    if (c === null || c === undefined || c === false) continue;
    if (Array.isArray(c)) append(el, c);
    else el.appendChild(c instanceof Node ? c : document.createTextNode(String(c)));
  }
}

const ICONS: Record<string, string> = {
  home: "M3 11 12 4l9 7v9a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z",
  users: "M16 19v-1a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v1M9 10a3 3 0 1 0 0-6 3 3 0 0 0 0 6m13 9v-1a4 4 0 0 0-3-3.9M16 4.1a3 3 0 0 1 0 5.8",
  chart: "M6 20V11M12 20V5M18 20v-6",
  more: "M5 12h.01M12 12h.01M19 12h.01",
  doc: "M7 3h7l5 5v13H7zM14 3v5h5M10 13h6M10 17h6",
  clock: "M12 7v5l3 2M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18",
  link: "M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1",
  user: "M20 21v-1a5 5 0 0 0-5-5H9a5 5 0 0 0-5 5v1M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8",
  phone: "M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2",
  info: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18M12 11v6M12 7.5v.01",
  alert: "M12 3 2 20h20zM12 10v4M12 17v.01",
  shield: "M12 3 4 6v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V6z",
  coins: "M12 8c4.4 0 8-1.3 8-3s-3.6-3-8-3-8 1.3-8 3 3.6 3 8 3M4 5v6c0 1.7 3.6 3 8 3s8-1.3 8-3V5M4 11v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6",
  check: "M5 12.5 10 17 19 7",
  back: "M15 5 8 12l7 7",
  hourglass: "M7 3h10M7 21h10M8 3c0 5 8 5 8 9s-8 4-8 9M16 3c0 5-8 5-8 9",
  chevron: "M9 6l6 6-6 6",
  lock: "M6 11h12v10H6zM8 11V7a4 4 0 0 1 8 0v4",
};

export function icon(name: string, cls = "ic"): SVGSVGElement {
  const ns = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(ns, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("class", cls);
  svg.setAttribute("aria-hidden", "true");
  const p = document.createElementNS(ns, "path");
  p.setAttribute("d", ICONS[name] ?? ICONS.info);
  svg.appendChild(p);
  return svg;
}

export function chip(text: string, tone: "amber" | "green" | "teal" | "red" | "grey" | "navy" = "grey", iconName?: string): HTMLElement {
  return h("span", { class: `chip chip-${tone}` }, iconName ? icon(iconName, "ic ic-sm") : null, text);
}

/** IP01: pilot presentation state (from /api/ip01/me; every rule is still enforced by the server). */
export const pilotState: { pilot: boolean; label: string; stopped: boolean } = { pilot: false, label: "", stopped: false };

export function demoChip(): HTMLElement {
  if (pilotState.pilot) return h("span", { class: "chip chip-amber", "data-testid": "pilot-label" }, pilotState.stopped ? "PILOT ĐANG DỪNG · CHỈ XEM" : "PILOT · KHO THẬT · DEMO · 0 VND THẬT");
  return chip("DỮ LIỆU DEMO", "teal");
}

export function fmtTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("vi-VN", { timeZone: "Asia/Ho_Chi_Minh", hour12: false, hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit", year: "numeric" });
}

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("vi-VN", { timeZone: "Asia/Ho_Chi_Minh" });
}

export function fmtVnd(n: number | null | undefined): string {
  return n === null || n === undefined ? "—" : `${n.toLocaleString("vi-VN")}đ`;
}

export function age(seconds: number): string {
  if (seconds < 3600) return `${Math.max(1, Math.round(seconds / 60))} phút`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)} giờ`;
  return `${Math.round(seconds / 86400)} ngày`;
}

export function initials(name: string): string {
  return name.replace(/\(.*?\)/g, "").trim().split(/\s+/).slice(-2).map((w) => w[0]?.toUpperCase() ?? "").join("");
}

export function errorBox(e: unknown): HTMLElement {
  const err = e as ApiError;
  const tone = err.hold ? "hold" : err.status === 403 || err.status === 401 ? "denied" : "error";
  const title = err.hold ? "ĐANG HOLD" : err.status === 403 ? "KHÔNG ĐƯỢC PHÉP" : err.status === 409 ? "CẦN TẢI LẠI / XUNG ĐỘT" : "CHƯA THỰC HIỆN ĐƯỢC";
  return h("div", { class: `banner banner-${tone}`, role: "alert", "data-code": err.code ?? "ERROR" },
    icon(err.hold ? "lock" : "alert"),
    h("div", {}, h("strong", {}, title), h("p", {}, err.message ?? "Đã có lỗi."), h("small", { class: "mono" }, `Mã: ${err.code ?? "UNKNOWN"}`)));
}

export function navigate(path: string): void {
  history.pushState({}, "", path);
  window.dispatchEvent(new Event("b7b:navigate"));
}

export function link(path: string, cls: string, ...children: Child[]): HTMLElement {
  return h("a", { href: path, class: cls, onclick: (ev: Event) => { ev.preventDefault(); navigate(path); } }, ...children);
}

export function sheet(title: string, body: HTMLElement): { el: HTMLElement; close: () => void } {
  const backdrop = h("div", { class: "sheet-backdrop" });
  const panel = h("div", { class: "sheet", role: "dialog", "aria-label": title },
    h("div", { class: "sheet-head" }, h("strong", {}, title), h("button", { class: "btn-text", onclick: () => close() }, "Đóng")), body);
  // A sheet belongs to the screen that opened it: close it on any navigation.
  const close = () => { backdrop.remove(); panel.remove(); window.removeEventListener("b7b:navigate", close); window.removeEventListener("popstate", close); };
  backdrop.addEventListener("click", close);
  window.addEventListener("b7b:navigate", close);
  window.addEventListener("popstate", close);
  document.body.append(backdrop, panel);
  return { el: panel, close };
}

export const REASON_LABEL: Record<string, string> = {
  AVAILABILITY_ACTIVATION: "Cần xác nhận khả dụng / kích hoạt",
  DEEP_CONSULT: "Tư vấn chuyên sâu",
  PRICE_POLICY: "Hỏi về giá / chính sách",
  OTHER: "Lý do khác",
};

export const MISSING_LABEL: Record<string, string> = {
  CONTEXT_SUMMARY: "Tóm tắt nhu cầu của khách (ít nhất 10 ký tự)",
  CONSENT_CONTACT_BY_CTV: "Xác nhận khách đồng ý liên hệ",
  CONSENT_CONTACT_BY_CLOSER: "Xác nhận khách đồng ý để Anh Hiếu liên hệ",
};

export const HANDOFF_STATUS: Record<string, [string, "amber" | "green" | "teal" | "red" | "grey"]> = {
  REQUESTED: ["REQUESTED · Đã gửi yêu cầu", "amber"],
  NEEDS_INFO: ["NEEDS INFO · Cần bổ sung", "red"],
  PENDING: ["PENDING · Đang chờ nhận", "amber"],
  ACCEPTED: ["ACCEPTED · Đã nhận", "green"],
};

export const CHANNEL_LABEL: Record<string, string> = { FACEBOOK: "Facebook", ZALO: "Zalo", TIKTOK: "TikTok", PHONE: "Điện thoại", OTHER: "Khác" };

export const COMMAND_LABEL: Record<string, string> = {
  CREATE_LEAD: "Lead được tạo", FIXTURE_CREATE_LEAD: "Lead được tạo (fixture demo)", UPDATE_NEXT_ACTION: "Cập nhật việc tiếp theo",
  REQUEST_HANDOFF: "Gửi yêu cầu chuyển Anh Hiếu", SUPPLEMENT_HANDOFF: "Bổ sung thông tin bàn giao", HANDOFF_NEEDS_INFO: "Anh Hiếu yêu cầu bổ sung thông tin",
  ACCEPT_HANDOFF: "Anh Hiếu đã nhận bàn giao", APPROVE_SOURCE_VERSION: "Duyệt version thành CURRENT", CREATE_SOURCE_DRAFT: "Tạo bản DRAFT",
  RECORD_POST_REFERENCE: "Ghi nhận bài đăng",
};

// ---------------------------------------------------------------- Phase 2 shared labels / helpers
Object.assign(COMMAND_LABEL, {
  CONTENT_COPY: "Copy bài (không ghi nhận đã đăng)", CONTENT_PRACTICE: "Thực hành (không ghi nhận đã đăng)",
  RECORD_EXTERNAL_POST_ATTESTED: "Ghi nhận bài đã đăng ngoài ứng dụng", CREATE_PRACTICE_LEAD: "Tạo khách thực hành (giả lập)",
  CONSULT_ASSESS: "Ghi nhận tình huống tư vấn", ADD_CASE_INTERNAL_NOTE: "CTV gửi ghi chú nội bộ cho chuyên gia",
  CREATE_LESSON_CARD: "Tạo bài học riêng", REFRESH_LESSON_CARD: "Làm mới bài học riêng",
  RECORD_RESTRICTION_PROPOSAL: "Ghi đề xuất hạn chế (chưa có hiệu lực)", DRAFT_CONTENT: "Soạn nháp mẫu (fixture)",
});

export const SIGNAL_UI: Record<string, { label: string; tone: "green" | "amber" | "red"; icon: string }> = {
  GREEN: { label: "XANH · Có hướng dẫn đã duyệt", tone: "green", icon: "check" },
  AMBER: { label: "VÀNG · Cần thêm thông tin", tone: "amber", icon: "info" },
  RED: { label: "ĐỎ · Cần chuyên gia", tone: "red", icon: "alert" },
};

export const RED_REASON_UI: Record<string, string> = {
  CONFLICTING_ANALYSIS: "Phân tích mâu thuẫn",
  MISSING_OR_UNVERIFIED_SOURCE: "Thiếu dữ liệu gốc / nguồn chưa xác minh",
  BEYOND_STANDARD_CONCLUSION: "Khách cần kết luận vượt nội dung đã duyệt",
  SPECIALIST_DEPTH: "Ca chuyên môn sâu",
  CTV_NOT_SURE: "Bạn bấm “Tôi không chắc”",
};

export function practiceChip(): HTMLElement {
  return h("span", { class: "chip chip-navy", "data-testid": "practice-label" }, "THỰC HÀNH");
}

/** Copies text if the browser allows it; otherwise shows the text selected so the user can copy by hand. */
export async function copyText(text: string): Promise<boolean> {
  try { await navigator.clipboard.writeText(text); return true; } catch { return false; }
}
